import { Component, ViewEncapsulation, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from 'app/core/auth/auth.service';
import { KossayaService } from 'app/core/kossaya/kossaya.service';

export interface FloorTheme {
    primary: string;
    primaryDark: string;
    primaryLight: string;
    gradient: string;
    cardBg: string;
    navActive: string;
}

/**
 * Reusable mobile layout for tenant/user pages.
 * Provides: Hero header, bottom navigation, theme colors.
 * Child pages render inside <router-outlet>.
 */
@Component({
    selector: 'user-layout',
    templateUrl: './user-layout.component.html',
    styleUrls: ['./user-layout.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class UserLayoutComponent implements OnInit {
    user: any = null;
    loading: boolean = true;

    // Tenant data
    roomLabel: string = '';
    roomFloor: number = 0;
    occupantName: string = '';
    occupantImage: string = '';
    contractStatus: string = '';
    contractEndDate: string = '';

    // Bottom nav
    activeNav: 'home' | 'notifications' | 'info' = 'home';

    // Theme based on floor
    floorThemes: { [key: number]: FloorTheme } = {
        1: {
            primary: '#00A2FF',
            primaryDark: '#0090FF',
            primaryLight: '#DBEAFE',
            gradient: 'linear-gradient(135deg, #00A2FF 0%, #33B5FF 50%, #66C8FF 100%)',
            cardBg: '#00A2FF',
            navActive: '#00A2FF'
        },
        2: {
            primary: '#A78D00',
            primaryDark: '#C4A500',
            primaryLight: '#FEF3C7',
            gradient: 'linear-gradient(135deg, #A78D00 0%, #E6B422 50%, #F0CC5B 100%)',
            cardBg: '#A78D00',
            navActive: '#A78D00'
        },
        3: {
            primary: '#0C463D',
            primaryDark: '#017C6F',
            primaryLight: '#D1FAE5',
            gradient: 'linear-gradient(135deg, #0C463D 0%, #3AAFA9 50%, #5CC8C4 100%)',
            cardBg: '#017C6F',
            navActive: '#017C6F'
        }
    };

    get theme(): FloorTheme {
        return this.floorThemes[this.roomFloor] || this.floorThemes[1];
    }

    constructor(
        private authService: AuthService,
        private router: Router,
        public kossayaService: KossayaService
    ) {}

    async ngOnInit(): Promise<void> {
        this.user = this.authService.getCurrentUser();
        if (!this.user) {
            this.router.navigate(['/sign-in']);
            return;
        }

        // Detect active nav from current URL
        this.detectActiveNav(this.router.url);

        // Listen to route changes to update active nav
        this.router.events
            .pipe(filter(e => e instanceof NavigationEnd))
            .subscribe((e: NavigationEnd) => {
                this.detectActiveNav(e.urlAfterRedirects || e.url);
            });

        await this.kossayaService.waitForInit();
        await this.loadTenantData();
    }

    /** Detect which nav item is active based on URL */
    private detectActiveNav(url: string): void {
        if (url.includes('/user/notifications')) {
            this.activeNav = 'notifications';
        } else if (url.includes('/user/info')) {
            this.activeNav = 'info';
        } else {
            this.activeNav = 'home';
        }
    }

    // Dashboard data for child pages
    dashboard: any = null;

    async loadTenantData(): Promise<void> {
        this.loading = true;
        try {
            const data = await this.kossayaService.getOccupantDashboard();
            if (data) {
                this.dashboard = data;

                // Occupant info
                this.occupantName = data.occupant?.name || this.user?.name || 'Penghuni';
                this.occupantImage = data.occupant?.image || '';

                // First active contract
                const contract = data.contracts?.find((c: any) => c.status === 'ACTIVE') || data.contracts?.[0];
                if (contract) {
                    this.contractStatus = contract.status || '';
                    this.contractEndDate = contract.end_date || '';

                    // Room info from first rental_contract_rooms
                    const rcRoom = contract.rental_contract_rooms?.[0];
                    if (rcRoom?.room) {
                        this.roomLabel = rcRoom.room.label || '';
                        // Determine floor from floor label (e.g., "Lantai 3" → 3)
                        const floorLabel = rcRoom.room.floor?.label || '';
                        const floorMatch = floorLabel.match(/\d+/);
                        this.roomFloor = floorMatch ? parseInt(floorMatch[0], 10) : 1;
                    }
                }
            } else {
                // Fallback
                this.occupantName = this.user?.name || 'Penghuni';
            }
        } catch (e) {
            console.error('Failed to load tenant data', e);
            this.occupantName = this.user?.name || 'Penghuni';
        } finally {
            this.loading = false;
        }
    }

    getInitials(): string {
        const parts = this.occupantName.split(' ');
        return parts.map(p => p.charAt(0).toUpperCase()).slice(0, 2).join('');
    }

    formatDate(dateStr: string): string {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    }

    setActiveNav(nav: 'home' | 'notifications' | 'info'): void {
        this.activeNav = nav;
        const routeMap: Record<string, string> = {
            home: '/user/home',
            notifications: '/user/notifications',
            info: '/user/info',
        };
        this.router.navigate([routeMap[nav]]);
    }

    logout(): void {
        this.authService.signOut().subscribe(() => {
            this.router.navigate(['/sign-in'], { replaceUrl: true });
        });
    }
}
